import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { List } from 'immutable';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import {
  UserAnnotation,
  UserReadingIntention,
  UserStudyNote,
} from './user-annotation';
import {
  DeleteReadingIntentionAction,
  DeleteReadingIntentionFailedAction,
  DeleteReadingIntentionSucceededAction,
  DeleteStudyNoteAction,
  DeleteStudyNoteFailedAction,
  DeleteStudyNoteSucceededAction,
  FetchUserAnnotationsForListAction,
  UpdateReadingIntentionAction,
  UpdateReadingIntentionFailedAction,
  UpdateReadingIntentionSucceededAction,
  UpdateStudyNoteAction,
  UpdateStudyNoteFailedAction,
  UpdateStudyNoteSucceededAction,
  UserAnnotationsActionTypes,
  UserAnnotationsForListLoadedAction,
  UserAnnotationsForListLoadingFailedAction,
} from './user-annotations.actions';
import { UserAnnotationsService } from './user-annotations.service';

@Injectable()
export class UserAnnotationsEffects {
  fetchUserAnnotationsForList$: Observable<
    | UserAnnotationsForListLoadedAction
    | UserAnnotationsForListLoadingFailedAction
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnotationsActionTypes.FETCH_USER_ANNOTATIONS_FOR_LIST),
      mergeMap((action: FetchUserAnnotationsForListAction) => {
        const { listId, userId } = action.payload;
        return this.userAnnotationsService
          .getUserAnnotations(listId, userId)
          .pipe(
            map(userAnnotations => {
              const readingIntentions = userAnnotations.filter(
                annotation => annotation instanceof UserReadingIntention,
              );
              const studyNotes = userAnnotations.filter(
                annotation => annotation instanceof UserStudyNote,
              );
              const readingIntentionsByResource =
                this.keyByResource<UserReadingIntention>(readingIntentions);
              const studyNotesByResource =
                this.keyByResource<UserStudyNote>(studyNotes);
              return new UserAnnotationsForListLoadedAction({
                ...action.payload,
                readingIntentionsByResource,
                studyNotesByResource,
              });
            }),
            catchError(error =>
              observableOf(
                new UserAnnotationsForListLoadingFailedAction({
                  ...action.payload,
                  error,
                }),
              ),
            ),
          );
      }),
    ),
  );

  updateReadingIntention$: Observable<
    UpdateReadingIntentionSucceededAction | UpdateReadingIntentionFailedAction
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnotationsActionTypes.UPDATE_READING_INTENTION),
      mergeMap((action: UpdateReadingIntentionAction) => {
        const { userId, resourceId, status } = action.payload;
        return this.userAnnotationsService
          .updateReadingIntentionStatus(userId, resourceId, status)
          .pipe(
            map(
              () => new UpdateReadingIntentionSucceededAction(action.payload),
            ),
            catchError(error =>
              observableOf(
                new UpdateReadingIntentionFailedAction({
                  ...action.payload,
                  error,
                }),
              ),
            ),
          );
      }),
    ),
  );

  deleteReadingIntention$: Observable<
    DeleteReadingIntentionSucceededAction | DeleteReadingIntentionFailedAction
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnotationsActionTypes.DELETE_READING_INTENTION),
      mergeMap((action: DeleteReadingIntentionAction) => {
        const { userId, resourceId } = action.payload;
        return this.userAnnotationsService
          .deleteReadingIntention(userId, resourceId)
          .pipe(
            map(
              () => new DeleteReadingIntentionSucceededAction(action.payload),
            ),
            catchError(error =>
              observableOf(
                new DeleteReadingIntentionFailedAction({
                  ...action.payload,
                  error,
                }),
              ),
            ),
          );
      }),
    ),
  );

  updateStudyNote$: Observable<
    UpdateStudyNoteSucceededAction | UpdateStudyNoteFailedAction
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnotationsActionTypes.UPDATE_STUDY_NOTE),
      mergeMap((action: UpdateStudyNoteAction) => {
        const { userId, resourceId, note } = action.payload;
        return this.userAnnotationsService
          .updateStudyNoteContent(userId, resourceId, note)
          .pipe(
            map(() => new UpdateStudyNoteSucceededAction(action.payload)),
            catchError(error =>
              observableOf(
                new UpdateStudyNoteFailedAction({
                  ...action.payload,
                  error,
                }),
              ),
            ),
          );
      }),
    ),
  );

  deleteStudyNote$: Observable<
    DeleteStudyNoteSucceededAction | DeleteStudyNoteFailedAction
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnotationsActionTypes.DELETE_STUDY_NOTE),
      mergeMap((action: DeleteStudyNoteAction) => {
        const { userId, resourceId } = action.payload;
        return this.userAnnotationsService
          .deleteStudyNote(userId, resourceId)
          .pipe(
            map(() => new DeleteStudyNoteSucceededAction(action.payload)),
            catchError(error =>
              observableOf(
                new DeleteStudyNoteFailedAction({
                  ...action.payload,
                  error,
                }),
              ),
            ),
          );
      }),
    ),
  );

  private keyByResource<T extends UserAnnotation<T>>(
    annotations: UserAnnotation<T>[],
  ): {
    [resourceId: string]: T;
  } {
    return List(annotations)
      .groupBy(annotation => annotation.resource.id)
      .map(groupedValue => groupedValue.last(null))
      .filter(value => (value ? true : false))
      .toJSON();
  }

  constructor(
    private actions$: Actions,
    private userAnnotationsService: UserAnnotationsService,
  ) {}
}
